import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { EConfigurationChannel, LICENSE_KEYS, LicenseKey } from '@web/dtos';
import { catchError, filter, map, of, take } from 'rxjs';
import { ERoute } from '../constants/routes.enum';
import { LicenseStore } from '../services/api/license/license-store.service';

export type LicenseKeyResolver = (
  route: ActivatedRouteSnapshot
) => string | readonly string[] | null | undefined;

function isLicenseKey(key: string): key is LicenseKey {
  return Object.values(LICENSE_KEYS).some(knownKey => knownKey === key);
}

/**
 * Requires at least one active license in the specified channel, or either channel when omitted.
 * Use in canActivate or canActivateChild; authentication must already be established.
 * Dynamic example: licenseGuard(route => route.paramMap.get('licenseKey'))
 * Example: canActivate: [licenseGuard(LICENSE_KEYS.Base, EConfigurationChannel.Web)]
 */
export function licenseGuard(
  licenseKeys: LicenseKey | readonly LicenseKey[] | LicenseKeyResolver,
  channel?: EConfigurationChannel
): CanActivateFn {
  return route => {
    const licenseStore = inject(LicenseStore);
    const router = inject(Router);
    const accessDenied = router.createUrlTree([`/${ERoute.accessDenied}`]);

    return toObservable(licenseStore.isLoading).pipe(
      filter(isLoading => !isLoading),
      take(1),
      map(() => {
        const resolved =
          typeof licenseKeys === 'function' ? licenseKeys(route) : licenseKeys;
        const keys = typeof resolved === 'string' ? [resolved] : resolved;
        if (!keys || keys.length === 0 || !keys.every(isLicenseKey)) {
          return accessDenied;
        }
        const isActive = licenseStore.hasAnyActiveLicense(keys, channel);
        return isActive ? true : accessDenied;
      }),
      catchError(() => of(accessDenied))
    );
  };
}
